import getChosenPriceBreakExpiration from "@/app/ec/product/[basePath]/vendor-options/getChosenPriceBreakExpiration";
import getStockedMessage, { StockedState } from "@/app/ec/product/[basePath]/vendor-options/getStockedMessage";
import type { VendorPurchase } from "@/store/sellerOptionsSlice";
import getShippingSummary, { getTotalShipping } from "../pricing/getShippingSummary";

export type SellerOptionFilter =
  | "authorized"
  | "freeDelivery"
  | "excludeLongHandling"
  | "excludeExpiringSoon"
  | "purchasedBefore";

export type SellerOptionSort = "recommended" | "lowestTotalCost" | "fastestDelivery" | "topRated";

/** Handling time, in business days, that the new PDP labels as long and "Exclude Long Handling" leaves out. */
const LONG_HANDLING_DAYS = 15;

/** What the filters need to know beyond the offer itself. */
export interface SellerOptionContext {
  /** The signed-in shopper's purchases, by vendor id. */
  vendorPurchases: ReadonlyMap<number, VendorPurchase>;
  now: Date;
}

interface FilterDefinition {
  label: string;
  /** Shown on hover while the filter is off. */
  description: string;
  /** Shown on hover when no offer passes the filter. */
  unavailableDescription: string;
  /** Whether an offer stays in the list while the filter is on. */
  keeps: (offer: VendorOptionState, context: SellerOptionContext) => boolean;
}

export const FILTERS: Record<SellerOptionFilter, FilterDefinition> = {
  authorized: {
    label: "Authorized Sellers",
    description: "Only show sellers who are authorized by the manufacturer to sell this item",
    unavailableDescription: "There are no sellers that are authorized by the manufacturer to sell this item",
    keeps: (offer) => offer.isAuthorizedDistributor,
  },
  freeDelivery: {
    label: "Free Delivery",
    description:
      "Only show sellers who offer free shipping for this item, or whose shipping fee is already covered by another item in your cart",
    unavailableDescription: "There are no sellers who offer free shipping for this item",
    keeps: (offer) => shipsFree(offer),
  },
  excludeLongHandling: {
    label: "Exclude Long Handling",
    description: `Don’t show sellers whose handling time is ${LONG_HANDLING_DAYS} days or longer, or who are backordered`,
    unavailableDescription: `There are no sellers whose handling time is less than ${LONG_HANDLING_DAYS} days for this item`,
    keeps: (offer) => !hasLongHandling(offer),
  },
  excludeExpiringSoon: {
    label: "Exclude Expiring Soon",
    description: "Don’t show seller offers with products expiring in less than 12 months",
    unavailableDescription: "There are no seller offers without products expiring in less than 12 months for this item",
    keeps: (offer, { now }) => !isExpiringSoon(offer, now),
  },
  purchasedBefore: {
    label: "Purchased From Before",
    description: "Only show sellers from whom you’ve previously made a purchase",
    unavailableDescription: "There are no sellers from whom you’ve previously made a purchase for this item",
    keeps: (offer, { vendorPurchases }) => vendorPurchases.has(offer.vendorId),
  },
};

export const SORT_OPTIONS: { value: SellerOptionSort; label: string }[] = [
  { value: "recommended", label: "Recommended" },
  { value: "lowestTotalCost", label: "Lowest Total Cost" },
  { value: "fastestDelivery", label: "Fastest Delivery" },
  { value: "topRated", label: "Top-Rated Sellers" },
];

/** The filters in the order they show. "Purchased From Before" is for signed-in shoppers only. */
export function getFilterOrder(isSignedIn: boolean): SellerOptionFilter[] {
  const filters: SellerOptionFilter[] = ["authorized", "freeDelivery", "excludeLongHandling", "excludeExpiringSoon"];
  return isSignedIn ? [...filters, "purchasedBefore"] : filters;
}

/** Offers that pass every active filter, in the chosen order. */
export function getVisibleOffers(
  offers: VendorOptionState[],
  filters: ReadonlySet<SellerOptionFilter>,
  sort: SellerOptionSort,
  context: SellerOptionContext,
): VendorOptionState[] {
  const kept = offers.filter((offer) => Array.from(filters).every((filter) => FILTERS[filter].keeps(offer, context)));
  return sortOffers(kept, sort);
}

/** A filter no offer passes can't narrow the list, so it shows disabled. */
export function isFilterAvailable(
  filter: SellerOptionFilter,
  offers: VendorOptionState[],
  context: SellerOptionContext,
): boolean {
  return offers.some((offer) => FILTERS[filter].keeps(offer, context));
}

// Array.prototype.sort is stable, so ties keep the recommended order neo returns.
export function sortOffers(offers: VendorOptionState[], sort: SellerOptionSort): VendorOptionState[] {
  switch (sort) {
    case "recommended":
      return offers;
    case "lowestTotalCost":
      return [...offers].sort((a, b) => a.productGrandTotal - b.productGrandTotal);
    case "fastestDelivery":
      return [...offers].sort((a, b) => handlingDays(a) - handlingDays(b));
    case "topRated":
      return [...offers].sort(
        (a, b) => rating(b) - rating(a) || b.vdrNumberOfGeneralRatings - a.vdrNumberOfGeneralRatings,
      );
  }
}

/** No standard shipping charge, whether free or covered by the cart, and no heavy-shipping fee. */
export function shipsFree(offer: VendorOptionState): boolean {
  const shipping = getShippingSummary(offer, null);
  return !shipping.heavyShippingUnknown && getTotalShipping(shipping) === 0;
}

/** The offer's stock line on the new PDP: long handling from LONG_HANDLING_DAYS, not the old PDP's 6. */
export function getOfferStockedState(offer: VendorOptionState): StockedState {
  return getStockedMessage(offer, LONG_HANDLING_DAYS);
}

/** What the offer's stock line calls long handling, plus backordered offers. */
export function hasLongHandling(offer: VendorOptionState): boolean {
  const state = getOfferStockedState(offer);
  return state === StockedState.LONG_HANDLING_TIME || state === StockedState.BACKORDERED;
}

/**
 * The backend gives expiration as MM/YYYY, so the product counts as good through the end of that month.
 * An unreadable date doesn't mark the offer as expiring.
 */
export function isExpiringSoon(offer: VendorOptionState, now: Date): boolean {
  const expiresAfter = getExpirationMonthEnd(offer);
  if (expiresAfter === null) {
    return false;
  }
  const yearFromNow = new Date(now);
  yearFromNow.setFullYear(yearFromNow.getFullYear() + 1);
  return expiresAfter < yearFromNow;
}

/** The first moment after the product's expiration month, or null without a valid expiration. */
export function getExpirationMonthEnd(offer: VendorOptionState): Date | null {
  const match = /^(\d{1,2})\/(\d{4})$/.exec(getChosenPriceBreakExpiration(offer) ?? "");
  if (!match) {
    return null;
  }
  const month = Number(match[1]);
  if (month < 1 || month > 12) {
    return null;
  }
  return new Date(Number(match[2]), month, 1);
}

// Backordered offers have no handling time to compare, so they sort last.
function handlingDays(offer: VendorOptionState): number {
  return offer.isBackordered ? Number.POSITIVE_INFINITY : offer.shippingTime;
}

// Sellers without reviews sort below every rated seller.
function rating(offer: VendorOptionState): number {
  return offer.vdrNumberOfGeneralRatings > 0 ? offer.vendorRating : -1;
}
